import type { CSSProperties } from 'react';
import './semantic.groupB.css';

/**
 * Foundation semantic: StatusMeter（Phase D §4.4 / Phase B 凍結対象 API）
 *
 * 複数の状態量を縦に並べて見せる横棒メーター群。
 * 例: 信頼度・残体力・収集進捗 などをまとめて可視化する。
 * type で色を切り替える：
 *   - positive → var(--mg-success)
 *   - negative → var(--mg-error)
 *   - neutral  → var(--mg-accent)（既定）
 * 配色は全て mood トークン経由（hex 直書きはしない）。
 */
export type StatusMeterType = 'positive' | 'negative' | 'neutral';

export interface StatusMeterItem {
  /** メーターの一意 ID（key にも使う） */
  id: string;
  /** 表示ラベル */
  label: string;
  /** 現在値 */
  current: number;
  /** 最大値（既定 100） */
  max?: number;
  /** 色種別（既定 'neutral'） */
  type?: StatusMeterType;
}

export interface StatusMeterProps {
  /** 縦に並べるメーター配列 */
  meters: StatusMeterItem[];
  /** バー高（px、既定 8） */
  barHeight?: number;
  /** 右側に数値（current/max）を出すか（既定 false） */
  showValue?: boolean;
  /** 追加 className */
  className?: string;
}

/** 0..max を 0..100 にクランプ（max<=0 は 0 扱い） */
function toPercent(current: number, max: number): number {
  if (!Number.isFinite(current) || !Number.isFinite(max) || max <= 0) return 0;
  const raw = (current / max) * 100;
  if (raw < 0) return 0;
  if (raw > 100) return 100;
  return raw;
}

export function StatusMeter({
  meters,
  barHeight = 8,
  showValue = false,
  className,
}: StatusMeterProps) {
  const classes = ['mg-meter-list', className].filter(Boolean).join(' ');
  // バー高は CSS 変数で全行に渡す（行ごとに上書きしない＝全体一貫の見た目）
  const listStyle: CSSProperties = {
    ['--mg-meter-bar-h' as string]: `${barHeight}px`,
  };

  return (
    <div className={classes} style={listStyle}>
      {meters.map((m) => {
        const safeMax = (m.max ?? 100) > 0 ? (m.max ?? 100) : 100;
        const pct = toPercent(m.current, safeMax);
        const type: StatusMeterType = m.type ?? 'neutral';
        // 動的な幅のみ CSS 変数で渡す（色は data-type → CSS 側で token に解決）
        const fillStyle: CSSProperties = {
          ['--mg-meter-fill' as string]: `${pct}%`,
        };

        return (
          <div key={m.id} className="mg-meter" data-type={type}>
            <div className="mg-meter__head">
              <span className="mg-meter__label">{m.label}</span>
              {showValue ? (
                <span className="mg-meter__value">
                  {Math.round(m.current)} / {safeMax}
                </span>
              ) : null}
            </div>
            <div
              className="mg-meter__track"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={safeMax}
              aria-valuenow={m.current}
              aria-label={m.label}
            >
              <div className="mg-meter__fill" style={fillStyle} />
            </div>
          </div>
        );
      })}
    </div>
  );
}
